import React, { useState, useEffect, useRef } from "react"
import { User, LogOut, Menu as Nav } from "react-feather"
import { Link, useLocation } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { loadEnvVars } from "../../utils/loadEnvVars"
import { Loading } from "@bancacfplus/Loading"
import { ReactComponent as Logo } from "@bancacfplus/Icons/assets/logo-horizontal.svg"
import { useTokenData } from "../../hooks/useTokenData"
import type { User as UserType } from "../../types"
import style from "./Menu.module.scss"

type SubMenuContainerProps = {
  isMobileOpened: boolean
  setIsMobileOpened: React.Dispatch<React.SetStateAction<boolean>>
  children: React.ReactNode
}

type MenuProps = {
  primaryMenu?: {
    text: string
    active?: boolean | undefined
    icon?: React.ReactNode | undefined
    external: boolean
    url: string
  }[]
  secondaryMenu?: {
    text: string
    url: string
    icon: React.ReactNode
  }[]
  user?: UserType
}

type MenuPrimaryProps = {
  primaryMenu: {
    text: string
    active?: boolean | undefined
    external: boolean
    url: string
    visible?: boolean
  }[]
}

type MenuSecondaryProps = {
  secondaryMenu: {
    text: string
    url: string
  }[]
  isMobileOpened: boolean
}

const MenuSecondary: React.FC<MenuSecondaryProps> = ({ secondaryMenu, isMobileOpened }) => {
  const [submenuOpened, setSubmenuOpened] = useState(false)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const { logout } = useAuth0()
  const { auth0RedirectUri: logoutURl } = loadEnvVars()

  const { data, error, isLoading } = useTokenData()

  useEffect(() => {
    function handleClickOutside(event: Event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setSubmenuOpened(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleWrapperBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextFocusedElement = event.relatedTarget as Node | null
    if (wrapperRef.current && !nextFocusedElement) {
      setSubmenuOpened(false)
      return
    }

    if (wrapperRef.current && nextFocusedElement && !wrapperRef.current.contains(nextFocusedElement)) {
      setSubmenuOpened(false)
    }
  }

  if (isLoading) return <Loading />

  if (error) throw error

  return (
    <>
      <li className={`${style.item} ${style.mobileOnly}`}>
        <div className={style.link}>
          <button
            className={style.subMenulink}
            onClick={() =>
              logout({
                logoutParams: {
                  returnTo: logoutURl
                }
              })
            }
          >
            <p className={style.subMenuItem}>Esci</p>
            <LogOut className={`${style.subMenuIcon} ${style.iconEnd}`} size={24} />
          </button>
        </div>
      </li>
      <li className={`${style.item} ${style.subMenuItem}`}>
        <div ref={wrapperRef} onBlur={handleWrapperBlur}>
          <button
            type="button"
            className={`${style.subMenuCta} ${submenuOpened ? style.isActive : ""}`}
            aria-label="Account"
            onClick={() => setSubmenuOpened(!submenuOpened)}
            onFocus={() => setSubmenuOpened(true)}
            aria-haspopup="true"
            aria-expanded={submenuOpened}
            aria-controls="account-submenu"
            data-test-id="menu-account"
          >
            <span className={`${style.link} ${style.lightLink} ${style.subMenuCtaText}`}>
              <User className={style.subMenuIcon} size={24} />
            </span>
          </button>
          <div
            id="account-submenu"
            className={`${style.subMenu} ${isMobileOpened ? style.isOpenedMobile : ""}`}
            aria-hidden={!submenuOpened}
          >
            <div className={style.subMenuGroup}>
              <div className={style.subMenuButton}>
                <p className={style.subMenuItemAccent}>{data?.email}</p>
                <p className={style.vatCode}>P.IVA {data?.partnerVatCode}</p>
              </div>
            </div>
            {secondaryMenu && secondaryMenu.length > 0 && (
              <div className={style.subMenuGroup}>
                {secondaryMenu.map(link => {
                  return (
                    <Link to={link.url} className={style.subMenulink} key={`id-${link.text}`} aria-label={link.text}>
                      {link.text}
                    </Link>
                  )
                })}
              </div>
            )}
            <div className={`${style.subMenuGroup} ${style.desktopOnly}`}>
              <button
                className={style.subMenulink}
                onClick={() =>
                  logout({
                    logoutParams: {
                      returnTo: logoutURl
                    }
                  })
                }
              >
                <p className={style.subMenuItem}>Esci</p>
                <LogOut className={`${style.subMenuIcon} ${style.iconEnd}`} size={24} />
              </button>
            </div>
          </div>
        </div>
      </li>
    </>
  )
}

const MenuPrimary: React.FC<MenuPrimaryProps> = ({ primaryMenu }) => {
  const { pathname } = useLocation()
  const rootPath = pathname.split("/")[1].replace("/", "")
  const page = primaryMenu?.findIndex(page => page.url === `/${rootPath}`)
  const [activePage, setActivePage] = useState<number>(page || 0)
  const visiblePages = primaryMenu?.filter(page => page.visible === true)

  const toggleClass = (indexPage: number) => {
    setActivePage(indexPage)
  }

  return (
    <div className={style.leftMenu}>
      {visiblePages ? (
        visiblePages?.map((link, index) => {
          return (
            <li
              className={`${style.item} ${index + 1 === visiblePages.length ? style.primaryLinksDivider : ""}`}
              key={`id-${link.text}`}
              tabIndex={-1}
            >
              {link.external ? (
                <a
                  href={link.url}
                  className={`${style.link} ${style.linkIconFixed} ${index === activePage ? style.isActive : ""}`}
                  onClick={() => toggleClass(index)}
                >
                  <span>{link.text}</span> <span className={style.menuIcon}>•</span>
                </a>
              ) : (
                <Link
                  to={link.url}
                  className={`${style.link} ${style.linkIconFixed} ${index === activePage ? style.isActive : ""}`}
                  onClick={() => toggleClass(index)}
                >
                  <span className={style.linkIconWrapper}>{link.text}</span>
                  <span className={style.menuIcon}>•</span>
                </Link>
              )}
            </li>
          )
        })
      ) : (
        <></>
      )}
    </div>
  )
}

type MenuLogoProps = {
  isPartnerPoste?: boolean
}

export const MenuLogo: React.FC<MenuLogoProps> = () => {
  return (
    <Link to="/" className={style.logo} tabIndex={0}>
      <div className={`${style.logoWrapper}`} aria-label="BancaCF+ Logo" role="button">
        <Logo className={style.logoIcon} />
      </div>
    </Link>
  )
}

const SubMenuContainer: React.FC<SubMenuContainerProps> = ({ isMobileOpened, setIsMobileOpened, children }) => {
  return (
    <>
      <nav className={`${style.linksContainer} ${isMobileOpened ? style.isOpened : ""}`} aria-label="Menu principale">
        <ul className={style.navItems}>{children}</ul>
      </nav>
      <button
        type="button"
        onClick={() => setIsMobileOpened(!isMobileOpened)}
        aria-haspopup="true"
        className={`${style.navToggler}`}
        aria-label="Menu"
      >
        <Nav />
        <span className={style.navText}>Menù</span>
      </button>
    </>
  )
}

type MenuWrapperProps = {
  children: React.ReactNode
}

export const MenuWrapper: React.FC<MenuWrapperProps> = ({ children }) => {
  return <div className={style.navHeader}>{children}</div>
}

const Menu: React.FC<MenuProps> = () => {
  const [isMobileOpened, setIsMobileOpened] = useState(false)

  useEffect(() => {
    function handleWindowResize() {
      if (window.innerWidth > 840 && isMobileOpened) {
        setIsMobileOpened(false)
      }
    }
    window.addEventListener("resize", handleWindowResize)
    return () => window.removeEventListener("resize", handleWindowResize)
  }, [isMobileOpened])

  return (
    <MenuWrapper>
      <MenuLogo />
      <SubMenuContainer isMobileOpened={isMobileOpened} setIsMobileOpened={setIsMobileOpened}>
        <MenuPrimary
          primaryMenu={[
            {
              text: "Dashboard",
              url: `/`,
              active: true,
              external: false,
              visible: true
            },
            {
              text: "Clienti",
              url: `/clients`,
              active: false,
              external: false,
              visible: true
            },
            {
              text: "Prevalutazione",
              url: "/prescoring",
              active: false,
              external: false,
              visible: true
            },
            {
              text: "Documenti",
              url: "/dashboard-documents",
              active: false,
              external: false,
              visible: true
            }
          ]}
        />
        <MenuSecondary
          secondaryMenu={[
            {
              text: "Dashboard",
              url: `/`
            },
            {
              text: "Clienti",
              url: `/clients`
            },
            {
              text: "Prevalutazione",
              url: "/prescoring"
            },
            {
              text: "Documenti",
              url: "/dashboard-documents"
            }
          ]}
          isMobileOpened={isMobileOpened}
        />
      </SubMenuContainer>
    </MenuWrapper>
  )
}

export { Menu }
